import type { ReactNode } from 'react';
import { cn } from '@/core/ui';
import { Eye, EyeSlash, WarningCircle } from '@/core/ui/icons';

/** Campos das telas de entrada (login e "Criar conta") — o mesmo visual nas duas. */
export const inputClass =
  'h-[3.25rem] w-full bg-transparent px-4 text-[0.95rem] text-fg placeholder:text-fg-subtle focus:outline-none';

/**
 * Campo preenchido, sem borda: o fundo marca a área e, no foco, clareia
 * com um halo suave na cor da marca.
 */
export function InputShell({ invalid, children }: { invalid: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        'relative rounded-2xl bg-surface-2 transition duration-200 hover:bg-surface-3',
        'focus-within:bg-surface-3 focus-within:shadow-[0_0_0_3px] focus-within:shadow-brand-400/45',
        invalid && 'bg-red-500/10 focus-within:shadow-red-400/20',
      )}
    >
      {children}
    </div>
  );
}

/** "Mostrar senha" — o olho dentro do campo de senha. */
export function RevealPasswordButton({ shown, onToggle }: { shown: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-label={shown ? 'Ocultar senha' : 'Mostrar senha'}
      className="absolute inset-y-0 right-1 flex w-11 items-center justify-center text-fg-subtle transition-colors hover:text-fg"
    >
      {shown ? <EyeSlash className="size-[1.1rem]" /> : <Eye className="size-[1.1rem]" />}
    </button>
  );
}

/** Mensagem de erro de um campo, ligada a ele por `aria-describedby`. */
export function FieldError({ id, children }: { id: string; children: ReactNode }) {
  return (
    <p id={id} className="text-xs text-red-400 light:text-red-600">
      {children}
    </p>
  );
}

/** Erro do envio, no topo do formulário. */
export function FormAlert({ children }: { children: ReactNode }) {
  return (
    <div
      role="alert"
      className="flex items-start gap-2 rounded-2xl bg-red-500/10 p-3 text-sm text-red-300 light:text-red-700"
    >
      <WarningCircle aria-hidden weight="fill" className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );
}
